/* CRM — воронка продаж, партнёры, тирлист, сквозные задачи, аккаунтинг.
   ТЗ 6.4, 6.4.1, 7.4, 7.8. */

import {
  accStageColor, accStagesOf, canEdit, canRead, crmLevel, dealColorScope, dealStagesOf, ddmm,
  DEV_KIND_META, devStagesOf, hasAnyFunnel, humanDate, isClosedStage, isSubscribedStage, rub,
  stageColor, stageTint, streamsOf, TIER_RANK,
} from "@mera/shared";
import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "../lib/router.jsx";

import { Ion } from "../components/icons.jsx";
import AddButton from "../components/AddButton.jsx";
import Kanban from "../components/Kanban.jsx";
import Screen from "../components/Screen.jsx";
import { SkelCrmAccounting, SkelCrmDeals, SkelKanban, SkelTasks } from "../components/skeleton.jsx";
import {
  Chips, Empty, ErrorState, Eyebrow, Hint, Metric, MetricGrid, Money, Pill, Sep,
  TabButton, TierBadge,
} from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useApp, useFetch, useSticky } from "../lib/store.jsx";
import { alertDialog, haptic } from "../lib/tg.js";

export default function Crm() {
  const { me, meta } = useApp();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState("funnel"); // funnel | tiers
  /* Поиск по доске: null — поле скрыто, строка — открыто с этим запросом.
     Sticky, чтобы после «назад» из найденной карточки вернуться к тем же результатам */
  const [search, setSearch] = useSticky("crm.search", null);

  // Бизнесы с заведённой воронкой (настраивается в «Управлении») и открытые правами
  const withFunnel = useMemo(
    () => (meta?.businesses || []).filter((b) => hasAnyFunnel(b) && canRead(crmLevel(me, b.id))),
    [meta, me]
  );

  /* Выбранные бизнес и поток живут в sticky-состоянии сеанса: заход в карточку
     и возврат — как и любой прыжок по разделам — не сбрасывают их. URL-параметры
     приходят только из внешних переходов (обзор ведёт в конкретную воронку)
     и одноразово поглощаются в sticky. */
  const [saved, setSaved] = useSticky("crm.view", {});
  const askedFunnel = params.get("funnel");
  const askedStream = params.get("stream");
  React.useEffect(() => {
    if (!askedFunnel && !askedStream) return;
    setSaved((p) => ({
      ...p,
      // Внешний переход в другую воронку сбрасывает поток, как и тап по чипу
      ...(askedFunnel ? { funnel: askedFunnel, stream: undefined } : {}),
      ...(askedStream ? { stream: askedStream } : {}),
    }));
    setParams(new URLSearchParams(), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askedFunnel, askedStream]);

  const funnel = askedFunnel || saved.funnel || withFunnel[0]?.id || "";
  const business = withFunnel.find((b) => b.id === funnel) || null;

  // Воронки бизнеса минус закрытые правами: скрытый поток не показываем вовсе
  const streams = useMemo(
    () =>
      streamsOf(business)
        .filter((s) => canRead(crmLevel(me, business?.id, s.id)))
        .map((s) => ({ id: s.id, label: s.l })),
    [business, me]
  );

  // Воронка без этапов в CRM не показывается, поэтому поток берём только из живых
  const asked = askedStream || saved.stream;
  const stream = streams.some((s) => s.id === asked) ? asked : streams[0]?.id || "tasks";

  const setParam = (k, v) => {
    haptic.select();
    // Запрос относится к доске, которую искали: на другой он только путает
    setSearch(null);
    setSaved((p) => {
      const next = { ...p, [k]: v };
      // Сменили бизнес — поток может у него не существовать, сбрасываем на первый
      if (k === "funnel") delete next.stream;
      return next;
    });
  };

  const onTasks = stream === "tasks";
  // Продажи и партнёры — одна и та же доска сделок, различаются только этапами
  const onDeals = stream === "clients" || stream === "partners";
  // Правки текущего потока: добавление карточек и перетаскивание по колонкам
  const streamEdit = canEdit(crmLevel(me, business?.id, stream));

  return (
    <Screen logo>
      <Chips
        items={withFunnel.map((b) => ({ id: b.id, label: b.name, avatar: b.avatar }))}
        value={funnel}
        onChange={(v) => setParam("funnel", v)}
      />

      {/* Потоки-воронки. «Задачи» сюда не попадают — это не воронка, а сквозной
          список, и живёт он кнопкой в строке под чипами (ТЗ 6.4.1) */}
      <div className="hscroll flex gap-2 px-4 pb-2">
        {streams
          .filter((s) => s.id !== "tasks")
          .map((s) => {
            const active = s.id === stream;
            return (
              <button
                key={s.id}
                onClick={() => setParam("stream", s.id)}
                className="shrink-0 rounded-full px-3.5 py-1.5 text-[13px] font-semibold whitespace-nowrap active:opacity-70"
                style={{
                  background: active ? "var(--tg-button)" : "var(--tg-section-bg)",
                  color: active ? "var(--tg-button-text)" : "var(--tg-text)",
                }}
              >
                {s.label}
              </button>
            );
          })}
      </div>

      {/* Строка действий раздела. Стоит на одном месте — содержимое ниже не
          прыгает при переключении. Слева выгрузка в xlsx, воронка ↔ тирлист и поиск
          (только у сделок и партнёров), справа задачи; в задачах та же кнопка возвращает к воронке.
          На доске разработки строки нет — задачи там не нужны. */}
      {business && stream !== "dev" ? (
        <div className="px-4 pb-1 flex items-center gap-2">
          {onDeals ? (
            <>
              <ExportButton funnel={funnel} stream={stream} />
              <ActionButton
                className="ms-2"
                icon={Ion.funnel}
                label={view === "funnel" ? "Тирлист" : "Воронка"}
                onClick={() => { haptic.select(); setView(view === "funnel" ? "tiers" : "funnel"); }}
              />
              <ActionButton
                className="ms-2"
                icon={Ion.search}
                label="Поиск"
                onClick={() => { haptic.select(); setSearch(search === null ? "" : null); }}
              />
            </>
          ) : null}
          {streams.some((s) => s.id === "tasks") ? (
            <ActionButton
              className="ms-auto"
              icon={onTasks ? Ion.funnel : Ion.checkbox}
              label={onTasks ? "Воронка" : "Задачи"}
              onClick={() => setParam("stream", onTasks ? streams[0]?.id : "tasks")}
            />
          ) : null}
        </div>
      ) : null}

      {onDeals && search !== null ? (
        <SearchField
          value={search}
          onChange={setSearch}
          onClose={() => { haptic.select(); setSearch(null); }}
          placeholder={stream === "partners" ? "Партнёр, ЛПР или ответственный" : "Клиент, ЛПР или ответственный"}
        />
      ) : null}

      {!business ? (
        <Empty text="Ни у одного бизнеса не заведена воронка" />
      ) : onTasks ? (
        <TasksTab funnel={funnel} />
      ) : stream === "accounting" ? (
        <AccountingTab funnel={funnel} business={business} canMove={streamEdit} />
      ) : stream === "dev" ? (
        <DevTab funnel={funnel} business={business} canMove={streamEdit} />
      ) : (
        <DealsTab
          funnel={funnel}
          business={business}
          stream={stream}
          view={view}
          query={search || ""}
          canMove={streamEdit}
        />
      )}

      {onDeals && streamEdit ? (
        <AddButton
          align="right"
          overTabbar
          label={stream === "partners" ? "Новый партнёр" : "Новая сделка"}
          onClick={() => { haptic.medium(); navigate(`/edit/deals/new?funnel=${funnel}&stream=${stream}`); }}
        />
      ) : null}

      {stream === "dev" && streamEdit ? (
        <AddButton
          align="right"
          overTabbar
          label="Новая карточка"
          onClick={() => { haptic.medium(); navigate(`/edit/devTasks/new?biz=${funnel}`); }}
        />
      ) : null}
    </Screen>
  );
}

/* Кнопка-действие раздела: текст с иконкой, без подложки. Состояние показывает
   сама подпись — «Задачи» ↔ «Воронка», — поэтому подсветки выбранного нет. */
function ActionButton({ icon: Icon, label, onClick, className = "" }) {
  return (
    <button
      className={`inline-flex items-center gap-1 text-[13px] font-semibold active:opacity-60 ${className}`}
      style={{ color: "var(--tg-link)" }}
      onClick={onClick}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

/* Выгрузка воронки в xlsx. Файл, как и отчёты финансов, уходит документом
   в чат с ботом: скачанное внутри Telegram легко теряется */
function ExportButton({ funnel, stream }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (busy) return;
    haptic.light();
    setBusy(true);
    try {
      await api.post(`/exports/funnel?${new URLSearchParams({ funnel, stream })}`);
      haptic.success();
      alertDialog("Таблица воронки отправлена в чат с ботом.");
    } catch (e) {
      haptic.error();
      alertDialog(e.message);
    } finally {
      setBusy(false);
    }
  };
  return <ActionButton icon={Ion.cloudUpload} label={busy ? "Отправляем…" : "В xlsx"} onClick={send} />;
}

/* Поле поиска под строкой действий. Пустое открывается с фокусом — раз нажали
   «Поиск», следом будут печатать; с запросом (вернулись из карточки) клавиатуру
   не поднимаем. Крестик закрывает поиск целиком, а не только чистит строку */
function SearchField({ value, onChange, onClose, placeholder }) {
  return (
    <div className="px-4 pt-1 pb-2">
      <div className="card-tg flex items-center gap-2 px-3 py-2">
        <Ion.search size={16} style={{ color: "var(--tg-hint)" }} />
        <input
          autoFocus={!value}
          type="text"
          enterKeyHint="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          // «Найти» на клавиатуре прячет её: результаты уже на экране
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          placeholder={placeholder}
          className="flex-1 min-w-0 bg-transparent outline-none text-[15px]"
          style={{ color: "var(--tg-text)" }}
        />
        <button className="shrink-0 active:opacity-60" onClick={onClose} aria-label="Закрыть поиск">
          <Ion.close size={18} style={{ color: "var(--tg-hint)" }} />
        </button>
      </div>
    </div>
  );
}

// Поиск без учёта регистра и «ё»: «Семён» находится и по «семен»
const fold = (s) => String(s || "").toLowerCase().replace(/ё/g, "е");

/* Сделки под запрос: по компании, ЛПР и ответственному. Совпавшие с начала
   названия — выше, дальше в порядке доски */
function searchDeals(items, query) {
  const q = fold(query.trim());
  const hits = items.filter((d) => [d.company, d.lpr, d.owner].some((f) => fold(f).includes(q)));
  const head = hits.filter((d) => fold(d.company).startsWith(q));
  return [...head, ...hits.filter((d) => !head.includes(d))];
}

/* ── Клиенты и партнёры ── */
function DealsTab({ funnel, business, stream, view, query, canMove }) {
  const { data, loading, error, refetch } = useFetch("/crm/funnel", { funnel, stream });
  const navigate = useNavigate();

  // Этапы приходят от API (конфиг бизнеса), локальный расчёт — запасной путь.
  // Он же рисует колонки скелетона: этапы известны из настроек, ждём только сделки.
  const stages = data?.stages?.length ? data.stages : dealStagesOf(business, stream);
  const columns = stages.map((s, i) => ({
    key: s,
    title: s,
    color: stageTint(business, dealColorScope(stream), s, stageColor(i, stages.length)),
  }));

  /* Перенос карточки: колонка уходит на сервер целиком — там и этап, и порядок.
     Экран уже перерисован оптимистично, поэтому ответ только подтверждает раскладку,
     а ошибка возвращает ту, что лежит в базе. */
  const move = async (status, ids) => {
    try {
      await api.post("/deals/reorder", { funnel, stream, status, ids });
      haptic.success();
    } catch (e) {
      haptic.error();
      alertDialog(e.message);
    }
    refetch();
  };

  if (loading) return <SkelCrmDeals columns={columns} />;
  if (error) return <ErrorState text={error} onRetry={refetch} />;

  const { metrics, items } = data;

  /* Пока в поле есть запрос, вместо метрик и доски — найденное списком: две
     ленты подряд, и совпадения терялись бы над канбаном */
  if (query.trim()) {
    const found = searchDeals(items, query);
    if (!found.length) return <Empty text="Ничего не нашлось" icon={Ion.search} />;
    return (
      <div className="px-4">
        <DealRows list={found} onOpen={(id) => navigate(`/deal/${id}`)} />
      </div>
    );
  }

  return (
    <>
      <MetricGrid cols={3} className="pt-1">
        <Metric label="Сделок" value={metrics.deals} />
        <Metric label="Выручка" value={rub(metrics.revenue)} />
        <Metric label="MRR" value={rub(metrics.mrr)} color="var(--fin-blue)" />
      </MetricGrid>

      {!items.length ? (
        <Empty text={stream === "partners" ? "Партнёров пока нет" : "В этой воронке пока нет сделок"} />
      ) : view === "funnel" ? (
        <Kanban
          columns={columns.map((c) => ({ ...c, items: items.filter((d) => d.status === c.key) }))}
          render={(d) => <DealCardMini key={d.id} d={d} onClick={() => navigate(`/deal/${d.id}`)} />}
          sumOf={(d) => d.revenue}
          onMove={canMove ? move : undefined}
        />
      ) : (
        <TierList items={items} onOpen={(id) => navigate(`/deal/${id}`)} />
      )}
    </>
  );
}

/* ── Аккаунтинг: канбан проектов по стадиям (ТЗ 6.4) ── */
function AccountingTab({ funnel, business, canMove }) {
  const { data, loading, error, refetch } = useFetch("/crm/funnel", { funnel, stream: "accounting" });
  const navigate = useNavigate();

  const stages = data?.accStages?.length ? data.accStages : accStagesOf(business);
  const columns = stages.map((s) => ({
    key: s,
    title: s,
    color: stageTint(business, "acc", s, accStageColor(s, stages)),
    muted: isClosedStage(s),
  }));

  // Перенос — как у сделок: колонка целиком, стадия и порядок одним запросом
  const move = async (accStage, ids) => {
    try {
      await api.post("/projects/reorder", { businessId: funnel, accStage, ids });
      haptic.success();
    } catch (e) {
      haptic.error();
      alertDialog(e.message);
    }
    refetch();
  };

  if (loading) return <SkelCrmAccounting columns={columns} />;
  if (error) return <ErrorState text={error} onRetry={refetch} />;

  const p = data.portfolio;

  return (
    <>
      {/* У подписочных воронок показываем MRR, у проектных — чек в работе */}
      {!stages.some(isSubscribedStage) ? (
        <MetricGrid cols={3} className="pt-1">
          <Metric label="Проектов" value={p.projects} sub="не закрытых" />
          <Metric label="Чек в работе" value={rub(p.checkInWork)} />
          <Metric label="Пауза/закрыт" value={p.closed} />
        </MetricGrid>
      ) : (
        <MetricGrid cols={3} className="pt-1">
          <Metric label="MRR" value={rub(p.mrr)} color="var(--fin-blue)" />
          <Metric label="Факт выручка" value={rub(p.factRevenue)} color="var(--fin-green)" />
          <Metric label="Себестоимость" value={rub(p.cost)} color="var(--fin-red)" />
        </MetricGrid>
      )}

      {!data.items.length ? (
        <Empty text="Проектов в аккаунтинге нет" />
      ) : (
        <Kanban
          columns={columns.map((c) => ({ ...c, items: data.items.filter((x) => x.accStage === c.key) }))}
          sumOf={(x) => x.income}
          onMove={canMove ? move : undefined}
          render={(e) => (
            <button
              key={e.id}
              onClick={() => { haptic.light(); navigate(`/project/${e.id}`); }}
              className="w-full text-left card-tg px-3 py-2.5 active:opacity-70"
            >
              <div className="flex items-center gap-2">
                {e.dealTier ? <TierBadge tier={e.dealTier} size={18} /> : null}
                <span className="text-[14px] font-semibold truncate flex-1">{e.name}</span>
                {e.alarm ? <AlarmDot kind={e.alarm} /> : null}
              </div>
              <div className="mt-0.5 text-[12px] truncate" style={{ color: "var(--tg-hint)" }}>
                {e.type} · {e.owner}
              </div>
              <div className="mt-1.5 flex items-center justify-between">
                <Hint>чек</Hint>
                <span className="num text-[13px] font-semibold">{rub(e.income)}</span>
              </div>
              <div className="flex items-center justify-between">
                <Hint>прибыль</Hint>
                <Money value={e.profit} sign={e.profit >= 0 ? "in" : "out"} className="text-[13px]" />
              </div>
            </button>
          )}
        />
      )}
    </>
  );
}

/* ── Разработка: доска фич и багов по этапам бизнеса ── */
function DevTab({ funnel, business, canMove }) {
  const { data, loading, error, refetch } = useFetch("/crm/funnel", { funnel, stream: "dev" });
  const { today } = useApp();
  const navigate = useNavigate();

  const stages = data?.devStages?.length ? data.devStages : devStagesOf(business);
  const done = stages[stages.length - 1];
  const columns = stages.map((s, i) => ({
    key: s,
    title: s,
    color: stageTint(business, "dev", s, stageColor(i, stages.length)),
    muted: s === done,
  }));

  // Денег на доске нет, поэтому суммы в заголовках не считаем — только перенос
  const move = async (stage, ids) => {
    try {
      await api.post("/dev-tasks/reorder", { businessId: funnel, stage, ids });
      haptic.success();
    } catch (e) {
      haptic.error();
      alertDialog(e.message);
    }
    refetch();
  };

  if (loading) return <SkelKanban columns={columns} />;
  if (error) return <ErrorState text={error} onRetry={refetch} />;

  return (
    <>
      {!data.items.length ? (
        <Empty text="Карточек разработки нет" />
      ) : (
        <Kanban
          columns={columns.map((c) => ({ ...c, items: data.items.filter((t) => t.stage === c.key) }))}
          onMove={canMove ? move : undefined}
          render={(t) => {
            const checked = (t.checklist || []).filter((i) => i.done).length;
            // Просрочка: срок в прошлом, а карточка ещё не доехала до «Готово»
            const overdue = t.dueDate && today && t.dueDate < today && t.stage !== done;
            return (
              <button
                key={t.id}
                onClick={() => { haptic.light(); navigate(`/devtask/${t.id}`); }}
                className="w-full text-left card-tg px-3 py-2.5 active:opacity-70"
              >
                {/* Заголовку отдана вся строка: тип переехал вниз, и делить её больше не с кем */}
                <div className="text-[14px] font-semibold leading-snug">{t.title}</div>
                <div className="mt-0.5 text-[12px] truncate" style={{ color: "var(--tg-hint)" }}>
                  {t.who || "без исполнителя"}
                </div>
                {/* Низ карточки: слева тип, справа столбиком проект и срок */}
                <div className="mt-1.5 flex items-end justify-between gap-2">
                  {/* Тип — бейджем своего цвета */}
                  <span
                    className="shrink-0 rounded-md px-1.5 py-[2px] text-[11px] font-semibold"
                    style={{
                      color: DEV_KIND_META[t.kind]?.color || "var(--tg-hint)",
                      background: `color-mix(in srgb, ${DEV_KIND_META[t.kind]?.color || "var(--tg-hint)"} 14%, transparent)`,
                    }}
                  >
                    {t.kind}
                  </span>

                  <div className="flex flex-col items-end gap-1 min-w-0">
                    {/* Проект — на подложке: это ярлык принадлежности, а не текст строки */}
                    {t.projName ? (
                      <span
                        className="max-w-full truncate rounded-md px-1.5 py-[2px] text-[11px]"
                        style={{ background: "var(--tg-secondary-bg)", color: "var(--tg-hint)" }}
                      >
                        {t.projName}
                      </span>
                    ) : null}

                    <div className="flex items-center gap-2 shrink-0 text-[12px]" style={{ color: "var(--tg-hint)" }}>
                      {t.checklist?.length ? (
                        <span className="num inline-flex items-center gap-0.5">
                          <Ion.checkbox size={11} /> {checked}/{t.checklist.length}
                        </span>
                      ) : null}
                      {t.atts?.length ? (
                        <span className="num inline-flex items-center gap-0.5">
                          <Ion.attach size={11} /> {t.atts.length}
                        </span>
                      ) : null}
                      {/* Срок всегда красноватый — он про «надо успеть»; просроченный
                          горит в полную силу и жирным */}
                      {t.dueDate ? (
                        <span
                          className={`num whitespace-nowrap ${overdue ? "font-semibold" : ""}`}
                          style={{
                            color: overdue
                              ? "var(--fin-red)"
                              : "color-mix(in srgb, var(--fin-red) 70%, var(--tg-hint))",
                          }}
                        >
                          до {humanDate(t.dueDate)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </button>
            );
          }}
        />
      )}
    </>
  );
}

/* ── Сквозные задачи воронки (ТЗ 6.4.1) ── */
function TasksTab({ funnel }) {
  const { data, loading, error, refetch } = useFetch("/crm/tasks", { funnel });
  const navigate = useNavigate();
  const [tab, setTab] = useState(null);

  if (loading) return <SkelTasks />;
  if (error) return <ErrorState text={error} onRetry={refetch} />;

  const sections = [
    { key: "overdue", title: "Просрочено", color: "var(--fin-red)", items: data.overdue },
    { key: "today", title: "Сегодня", color: "var(--fin-gold)", items: data.today },
    { key: "next", title: "Дальше", color: "var(--fin-blue)", items: data.next },
  ];

  if (!sections.some((s) => s.items.length)) return <Empty text="Открытых задач нет" icon={Ion.checkbox} />;

  /* Вкладки, а не три списка подряд: срочность здесь взаимоисключающая, и до
     «Дальше» через всю просрочку никто не доскроллит. Пустые не прячем — ноль
     в просрочке сам по себе ответ. Открыта первая непустая, пока не выбрали другую. */
  const current = sections.find((s) => s.key === tab && s.items.length)
    || sections.find((s) => s.items.length);

  return (
    <>
      <div className="px-4 pt-1 flex gap-2">
        {sections.map((s) => (
          <TabButton
            key={s.key}
            compact
            active={s.key === current.key}
            onClick={() => { haptic.select(); setTab(s.key); }}
            color={s.color}
            label={s.title}
            count={s.items.length}
          />
        ))}
      </div>

      <div className="px-4 pt-3">
        <div className="card-tg py-1">
          {current.items.map((t, i) => (
            <React.Fragment key={t.id}>
              {i > 0 ? <Sep /> : null}
              <button
                className="w-full text-left px-4 py-2.5 flex items-center gap-3 active:opacity-60"
                onClick={() => {
                  haptic.light();
                  if (t.dealId) navigate(`/deal/${t.dealId}`);
                  else if (t.projectId) navigate(`/project/${t.projectId}`);
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: current.color }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-medium leading-snug">{t.title}</div>
                  <div className="mt-0.5 text-[12px] truncate" style={{ color: "var(--tg-hint)" }}>
                    {t.company || "—"} · {t.who || "—"}
                    {t.remind ? " · напоминание" : ""}
                  </div>
                </div>
                <span className="num text-[12px] shrink-0" style={{ color: current.color }}>
                  {t.dueDate ? ddmm(t.dueDate) : t.dueText || "—"}
                </span>
                <Ion.chevronForward size={16} style={{ color: "var(--tg-hint)" }} />
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>
    </>
  );
}

/* ── Карточка сделки в канбане (ТЗ 6.4) ──
   Без подписей: слева внизу крупно сумма сделки, справа зелёным прибыль —
   семантика цифр читается цветом и местом, слова только занимали бы строку. */
function DealCardMini({ d, onClick }) {
  return (
    <button
      onClick={() => { haptic.light(); onClick(); }}
      className="w-full text-left card-tg px-3 py-2.5 active:opacity-70"
    >
      <div className="flex items-center gap-2">
        <TierBadge tier={d.tier} size={18} />
        <span className="text-[14px] font-semibold truncate flex-1">{d.company}</span>
        {d.alarm ? <AlarmDot kind={d.alarm} /> : null}
      </div>
      {/* Дата добавления — «15 июля» на нейтральной подложке, рядом ответственный */}
      <div className="mt-1 flex items-center gap-1.5 min-w-0">
        {d.added ? (
          <span
            className="num shrink-0 rounded-md px-1.5 py-[2px] text-[11px] font-semibold"
            style={{ color: "var(--tg-hint)", background: "color-mix(in srgb, var(--tg-hint) 12%, transparent)" }}
          >
            {humanDate(d.added)}
          </span>
        ) : null}
        <span className="text-[12px] truncate" style={{ color: "var(--tg-hint)" }}>
          {d.owner || "без ответственного"}
        </span>
      </div>
      {d.mrr ? (
        <div className="mt-1.5">
          <Pill color="var(--fin-blue)" bg="color-mix(in srgb, var(--fin-blue) 12%, transparent)">
            MRR {rub(d.mrr)}
          </Pill>
        </div>
      ) : null}
      <div className="mt-1.5 flex items-baseline justify-between gap-2">
        <span className="num text-[17px] font-bold leading-tight truncate">{rub(d.revenue)}</span>
        <Money value={d.profit} sign="in" className="text-[13px] shrink-0" />
      </div>
    </button>
  );
}

/** Аларм по задачам: красный — просрочка, жёлтый — нет открытых задач (ТЗ 7.8) */
export function AlarmDot({ kind }) {
  const color = kind === "red" ? "var(--fin-red)" : "var(--fin-gold)";
  return <Ion.alertCircle size={15} style={{ color }} className="shrink-0" />;
}

/* ── Тирлист: группировка по приоритету клиента ── */
function TierList({ items, onOpen }) {
  const groups = useMemo(() => {
    const by = {};
    items.forEach((d) => {
      (by[d.tier] ||= []).push(d);
    });
    return Object.entries(by).sort(([a], [b]) => (TIER_RANK[a] ?? 9) - (TIER_RANK[b] ?? 9));
  }, [items]);

  return (
    <>
      {groups.map(([tier, list]) => (
        <div key={tier}>
          <Eyebrow>
            Тир {tier} · {list.length} · {rub(list.reduce((s, d) => s + d.revenue, 0))}
          </Eyebrow>
          <div className="px-4">
            <DealRows list={list} onOpen={onOpen} />
          </div>
        </div>
      ))}
    </>
  );
}

/* Сделки строками в одной карточке — общие для тирлиста и результатов поиска */
function DealRows({ list, onOpen }) {
  return (
    <div className="card-tg py-1">
      {list.map((d, i) => (
        <React.Fragment key={d.id}>
          {i > 0 ? <Sep /> : null}
          <button
            className="w-full text-left px-4 py-2.5 flex items-center gap-3 active:opacity-60"
            onClick={() => { haptic.light(); onOpen(d.id); }}
          >
            <TierBadge tier={d.tier} />
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-medium truncate">{d.company}</div>
              <div className="text-[12px] truncate" style={{ color: "var(--tg-hint)" }}>
                {d.status} · {d.owner}
              </div>
            </div>
            {d.alarm ? <AlarmDot kind={d.alarm} /> : null}
            <span className="num text-[14px] font-semibold shrink-0">{rub(d.revenue)}</span>
            <Ion.chevronForward size={16} style={{ color: "var(--tg-hint)" }} />
          </button>
        </React.Fragment>
      ))}
    </div>
  );
}
